/* import __COLOCATED_TEMPLATE__ from './referral-form.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action, getProperties } from '@ember/object';
import { dropTask } from 'ember-concurrency';
import { object, string, number } from 'yup';
import { validateSchema } from 'sh-www/utils/validators';
import { handleErrorResponse } from 'sh-www/utils/handles-errors';
import { DEPARTMENTS } from 'sh-www/models/campaign';

class ReferralForm extends Component {
  @service
  api;

  @tracked
  errors = null;

  @tracked
  first_name = '';

  @tracked
  last_name = '';

  @tracked
  email = '';

  @tracked
  phone_number = '';

  @tracked
  company_name = '';

  @tracked
  typed_department = null;

  @tracked
  selected_department = null;

  get filtered_departments() {
    const value = (this.typed_department ?? '').toLocaleLowerCase();

    return value
      ? DEPARTMENTS.filter(({ key }) => key.toLocaleLowerCase().includes(value))
      : DEPARTMENTS;
  }

  get department_name() {
    return this.typed_department ?? this.selected_department?.key ?? '';
  }

  get department() {
    return this.selected_department?.value;
  }

  @action
  setFormField(prop, value) {
    this[prop] = value;

    if (this.errors?.[prop]) {
      this.errors = { ...this.errors, [prop]: undefined };
    }
  }

  @action
  setDepartmentField(prop, value) {
    this[prop] = value;

    if (prop === 'typed_department' && value === '') {
      this.selected_department = null;
    } else if (prop === 'selected_department') {
      this.typed_department = value.key;
    }

    if (this.errors?.department) {
      this.errors = { ...this.errors, department: undefined };
    }
  }

  @dropTask()
  *handleSubmit(e) {
    e.preventDefault();

    const schema = object({
      'first_name': string()
        .required('Please enter your first name')
        .min(2, 'Please enter your first name'),

      'last_name': string()
        .required('Please enter your last name')
        .min(2, 'Please enter your last name'),

      'email': string()
        .required('Please enter a valid email')
        .email('Please enter a valid email'),

      'phone_number': string()
        .transform((x) => x || undefined)
        .optional()
        .min(7, 'Your phone number must be at least 7 digits'),

      'company_name': string()
        .required('Please enter a company name')
        .min(2, 'Please enter a company name'),

      'department': number()
        .optional()
        .oneOf(DEPARTMENTS.map(({ value }) => value)),
    });

    const [values, validationErrors = null] = validateSchema(getProperties(this, Object.keys(schema.fields)), schema);

    if (this.errors = validationErrors) {
      return;
    }

    const jsonData = { ...values, campaign_id: this.args.campaign.id };

    this.args.onLoading?.(true);
    const [, error] = yield this.submitForm(this.args.referrer.id, jsonData);
    this.args.onLoading?.(false);

    if (error) {
      handleErrorResponse(this, error, Object.keys(jsonData), false);
      this.args.onError?.(error);
      return;
    }

    this.args.onComplete?.();
  }

  async submitForm(referrerId, jsonData) {
    try {
      await this.api.post(`users/${referrerId}/refer`, { jsonData });
      return [true]
    } catch (e) {
      return [null, e];
    }
  }
}

export default ReferralForm;
